import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate, useParams } from "react-router-dom";

import EnglishQuizGroup from "./components/EnglishQuizGroup";
import EnglishQuizNavigation from "./components/EnglishQuizNavigation";
import EnglishQuizTimer from "./components/EnglishQuizTimer";

import {
  useResumeEnglishQuizAttemptQuery,
  useSubmitEnglishQuizAttemptMutation,
} from "../../slices/englishQuizAttemptApiSlice";


const EnglishQuizAttemptScreen = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  // ======================================================
  // Load Attempt
  // ======================================================

  const {
    data: attempt,
    isLoading,
    error,
  } = useResumeEnglishQuizAttemptQuery(attemptId);


  // ======================================================
  // Submit
  // ======================================================

  const [
    submitEnglishQuizAttempt,
    { isLoading: isSubmitting },
  ] = useSubmitEnglishQuizAttemptMutation();


  // ======================================================
  // State
  // ======================================================

  const [currentGroupIndex, setCurrentGroupIndex] =
    useState(0);

  const [answers, setAnswers] = useState({});


  // ======================================================
  // Groups
  // ======================================================

  const groups = useMemo(() => {
    return [...(attempt?.groups || [])].sort(
      (a, b) =>
        Number(a.order || 0) -
        Number(b.order || 0),
    );
  }, [attempt]);


  // ======================================================
  // Questions By Group
  // ======================================================

  const questionsByGroup = useMemo(() => {
    const result = {};

    for (const question of attempt?.questions || []) {
      const groupId =
        question.group?.toString();

      if (!groupId) {
        continue;
      }

      if (!result[groupId]) {
        result[groupId] = [];
      }

      result[groupId].push(question);
    }

    Object.keys(result).forEach((groupId) => {
      result[groupId].sort(
        (a, b) =>
          Number(a.order || 0) -
          Number(b.order || 0),
      );
    });

    return result;
  }, [attempt]);


  // ======================================================
  // Current Group
  // ======================================================

  const currentGroup =
    groups[currentGroupIndex];


  const currentGroupQuestions =
    currentGroup
      ? questionsByGroup[
          currentGroup.group?.toString()
        ] || []
      : [];


  // ======================================================
  // Restore Existing Answers
  // ======================================================

  useEffect(() => {
    if (!attempt?.answers?.length) {
      return;
    }

    const restoredAnswers = {};

    for (const answer of attempt.answers) {
      if (
        answer.question &&
        answer.selectedAnswer !== null &&
        answer.selectedAnswer !== undefined
      ) {
        restoredAnswers[
          answer.question.toString()
        ] = answer.selectedAnswer;
      }
    }

    setAnswers(restoredAnswers);
  }, [attempt]);


  // ======================================================
  // Answer Change
  // ======================================================

  const handleAnswerChange = (
    questionId,
    answer,
  ) => {
    setAnswers((previous) => ({
      ...previous,
      [questionId]: answer,
    }));
  };


  // ======================================================
  // Previous Group
  // ======================================================

  const handlePrevious = () => {
    if (currentGroupIndex > 0) {
      setCurrentGroupIndex(
        (index) => index - 1,
      );
    }
  };


  // ======================================================
  // Next Group
  // ======================================================

  const handleNext = () => {
    if (
      currentGroupIndex <
      groups.length - 1
    ) {
      setCurrentGroupIndex(
        (index) => index + 1,
      );
    }
  };


  // ======================================================
  // Submit
  // ======================================================

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    try {
      const answersPayload =
        Object.entries(answers).map(
          ([question, selectedAnswer]) => ({
            question,
            selectedAnswer,
          }),
        );

      await submitEnglishQuizAttempt({
        attemptId,
        answers: answersPayload,
      }).unwrap();

      navigate(
  `/english-quiz-attempts/${attemptId}/result`,
);

    } catch (error) {
      console.error(
        "Failed to submit English quiz:",
        error,
      );
    }
  };


  // ======================================================
  // Loading
  // ======================================================

  if (isLoading) {
    return (
      <div className="p-10 text-center">
        Loading quiz...
      </div>
    );
  }


  // ======================================================
  // Error
  // ======================================================

  if (error || !attempt) {
    return (
      <div className="p-10 text-center text-red-600">
        Unable to load this quiz attempt.
      </div>
    );
  }


  // ======================================================
  // Completed
  // ======================================================

  if (attempt.status === "completed") {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl bg-white p-8 shadow text-center">

            <h1 className="text-2xl font-bold text-gray-800">
              Quiz Completed
            </h1>

            <p className="mt-3 text-gray-600">
              This quiz attempt has already been
              submitted.
            </p>

          </div>
        </div>
      </div>
    );
  }


  // ======================================================
  // Render
  // ======================================================

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">

      <div className="max-w-5xl mx-auto">

        {/* ==================================================
            Header
        ================================================== */}

        <div
          className="
            mb-5
            flex
            flex-col
            gap-4
            md:flex-row
            md:items-center
            md:justify-between
          "
        >

          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {attempt.quizName || "English Quiz"}
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              Group {currentGroupIndex + 1} of{" "}
              {groups.length}
            </p>
          </div>


          <EnglishQuizTimer
            durationMinutes={
              attempt.durationMinutes
            }
            startedAt={
              attempt.startedAt
            }
            onTimeUp={handleSubmit}
          />

        </div>


        {/* ==================================================
            Current Group
        ================================================== */}

        {currentGroup ? (
          <EnglishQuizGroup
            group={currentGroup}
            questions={currentGroupQuestions}
            answers={answers}
            onAnswerChange={
              handleAnswerChange
            }
          />
        ) : (
          <div className="rounded-2xl bg-white p-8 shadow text-center">
            <p className="text-gray-600">
              No question groups available.
            </p>
          </div>
        )}


        {/* ==================================================
            Navigation
        ================================================== */}

        <EnglishQuizNavigation
          questions={groups}
          currentIndex={currentGroupIndex}
          answers={answers}
          onQuestionChange={
            setCurrentGroupIndex
          }
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />

      </div>

    </div>
  );
};


export default EnglishQuizAttemptScreen;